import {
  addDoc,
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const CHECKIN_COLLECTION = "checkins";
const DEFAULT_ORG_ID = "org_default";

export function checkinCollectionPath(orgId) {
  return `orgs/${orgId || DEFAULT_ORG_ID}/${CHECKIN_COLLECTION}`;
}

export function resolveCheckinOrgId(user, activeOrgId, role = "member") {
  const fallback = String(user?.orgId || "").trim() || DEFAULT_ORG_ID;
  const active = String(activeOrgId || "").trim();
  if (role !== "member") return active || fallback;
  const memberships = Array.from(new Set([
    user?.orgId,
    ...(Array.isArray(user?.orgIds) ? user.orgIds : []),
  ].map((orgId) => String(orgId || "").trim()).filter(Boolean)));
  if (active && memberships.includes(active)) return active;
  const nonDefaultMembership = memberships.find((orgId) => orgId && orgId !== DEFAULT_ORG_ID);
  if (fallback && fallback !== DEFAULT_ORG_ID) return fallback;
  return nonDefaultMembership || fallback || memberships[0] || active || DEFAULT_ORG_ID;
}

function logCheckinDiagnostic(event, meta = {}, level = "info") {
  try {
    const logger = level === "warn" ? console.warn : console.info;
    logger(`[checkins] ${event}`, meta);
  } catch {}
}

function now() {
  return Date.now();
}

function dayKey(ts = Date.now()) {
  return new Date(ts).toISOString().slice(0, 10);
}

function newest(rows = []) {
  return [...rows].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}

async function getCheckinDocFromServer(orgId, checkinId) {
  const snap = await getDocFromServer(doc(db, "orgs", orgId, CHECKIN_COLLECTION, String(checkinId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createCheckin(user, orgId, payload = {}) {
  if (!user?.uid || !orgId) return null;
  const writePath = checkinCollectionPath(orgId);
  logCheckinDiagnostic("checkInWritePath", { path: writePath });
  logCheckinDiagnostic("checkInOrgId", { orgId });
  logCheckinDiagnostic("checkInUserIdPresent", { present: Boolean(user?.uid) });
  const entry = {
    orgId,
    userId: user.uid,
    userEmail: user.email || "",
    userName: user.username || user.email?.split("@")[0] || "User",
    mood: payload.mood ?? 3,
    moodLabel: payload.moodLabel || "Okay",
    tags: Array.isArray(payload.tags) ? payload.tags : [],
    moodTags: Array.isArray(payload.moodTags) ? payload.moodTags : [],
    impactTags: Array.isArray(payload.impactTags) ? payload.impactTags : [],
    note: payload.note || "",
    createdAt: now(),
    dayKey: dayKey(),
  };
  try {
    const ref = await addDoc(collection(db, "orgs", orgId, CHECKIN_COLLECTION), entry);
    logCheckinDiagnostic("checkInSubmitSucceeded", {
      path: writePath,
      orgId,
      userIdPresent: Boolean(user?.uid),
      firestoreDocId: ref.id || "",
      addDocSucceeded: Boolean(ref.id),
    });
    if (!ref.id) {
      throw new Error("Firestore check-in write returned no document id.");
    }
    const confirmed = await getCheckinDocFromServer(orgId, ref.id);
    if (!confirmed?.id) {
      throw new Error("Firestore check-in write could not be confirmed by server readback.");
    }
    logCheckinDiagnostic("checkInSubmitReadbackSucceeded", {
      path: `${writePath}/${ref.id}`,
      orgId,
      userIdPresent: Boolean(user?.uid),
      firestoreDocId: ref.id,
    });
    return confirmed;
  } catch (error) {
    logCheckinDiagnostic("checkInSubmitFailed", {
      path: writePath,
      orgId,
      userIdPresent: Boolean(user?.uid),
      code: error?.code || "",
      message: error?.message || "",
    }, "warn");
    throw error;
  }
}

export async function fetchUserCheckinWindow(orgId, userId, days = 7) {
  if (!orgId || !userId) return [];
  const since = Date.now() - days * 24 * 60 * 60 * 1000;
  const readPath = checkinCollectionPath(orgId);
  logCheckinDiagnostic("checkInReadPath", { path: readPath, userScoped: true, days, simplifiedQuery: true });
  logCheckinDiagnostic("checkInOrgId", { orgId });
  logCheckinDiagnostic("checkInUserIdPresent", { present: Boolean(userId) });
  try {
    const base = collection(db, "orgs", orgId, CHECKIN_COLLECTION);
    const q = query(base, where("userId", "==", userId));
    const snap = await getDocsFromServer(q);
    const rows = newest(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => Number(r.createdAt || 0) >= since));
    logCheckinDiagnostic("checkInDashboardCount", { count: rows.length, path: readPath });
    return rows;
  } catch (error) {
    logCheckinDiagnostic("checkInReadFailed", {
      path: readPath,
      orgId,
      userIdPresent: Boolean(userId),
      code: error?.code || "",
      message: error?.message || "",
    }, "warn");
    throw error;
  }
}

export async function fetchUserCheckinRange(orgId, userId, startMs, endMs) {
  if (!orgId || !userId) return [];
  const readPath = checkinCollectionPath(orgId);
  logCheckinDiagnostic("checkInReadPath", { path: readPath, userScoped: true, startMs, endMs, simplifiedQuery: true });
  logCheckinDiagnostic("checkInOrgId", { orgId });
  logCheckinDiagnostic("checkInUserIdPresent", { present: Boolean(userId) });
  try {
    const base = collection(db, "orgs", orgId, CHECKIN_COLLECTION);
    const q = query(base, where("userId", "==", userId));
    const snap = await getDocsFromServer(q);
    return newest(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => Number(r.createdAt || 0) >= startMs && Number(r.createdAt || 0) <= endMs));
  } catch (error) {
    logCheckinDiagnostic("checkInReadFailed", {
      path: readPath,
      orgId,
      userIdPresent: Boolean(userId),
      code: error?.code || "",
      message: error?.message || "",
    }, "warn");
    throw error;
  }
}

export async function fetchRecentCheckins(orgId, { max = 20, userId } = {}) {
  if (!orgId) return [];
  const readPath = checkinCollectionPath(orgId);
  logCheckinDiagnostic("checkInReadPath", { path: readPath, userScoped: Boolean(userId), max, simplifiedQuery: Boolean(userId) });
  logCheckinDiagnostic("checkInOrgId", { orgId });
  logCheckinDiagnostic("checkInUserIdPresent", { present: Boolean(userId) });
  try {
    const base = collection(db, "orgs", orgId, CHECKIN_COLLECTION);
    const q = userId
      ? query(base, where("userId", "==", userId))
      : query(base, orderBy("createdAt", "desc"), limit(max));
    const snap = await getDocsFromServer(q);
    const rows = newest(snap.docs.map((d) => ({ id: d.id, ...d.data() }))).slice(0, max);
    logCheckinDiagnostic("checkInDashboardCount", { count: rows.length, path: readPath });
    return rows;
  } catch (error) {
    logCheckinDiagnostic("checkInReadFailed", {
      path: readPath,
      orgId,
      userIdPresent: Boolean(userId),
      code: error?.code || "",
      message: error?.message || "",
    }, "warn");
    throw error;
  }
}

export async function fetchCheckinWindow(orgId, days = 7) {
  if (!orgId) return [];
  const since = Date.now() - days * 24 * 60 * 60 * 1000;
  const readPath = checkinCollectionPath(orgId);
  logCheckinDiagnostic("checkInReadPath", { path: readPath, userScoped: false, days });
  logCheckinDiagnostic("checkInOrgId", { orgId });
  try {
    const base = collection(db, "orgs", orgId, CHECKIN_COLLECTION);
    const q = query(base, where("createdAt", ">=", since), orderBy("createdAt", "desc"));
    const snap = await getDocsFromServer(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    logCheckinDiagnostic("checkInReadFailed", {
      path: readPath,
      orgId,
      code: error?.code || "",
      message: error?.message || "",
    }, "warn");
    throw error;
  }
}
